import type { FilterPanelCheckItem } from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'
import { STANDALONE_FLIGHT_FILTERS, FlightFilterType } from 'constants/flight'
import { arrayToObject, last } from 'lib/array/arrayUtils'
import { formatCurrency } from 'lib/format/formatCurrencyIntl'
import { convertMinutesToHoursAndMinutes, convertMinutesToTimeString } from 'lib/flights/flightUtils'
import { getFilterQuantity } from 'lib/flights/flightFilterUtils'
import { FlightsFiltersPriceType } from 'lib/flights/flightFilterTypes'
import type { IntlShape, MessageDescriptor } from 'react-intl'
import { defineMessages } from 'react-intl'

interface LabelDescriptor {
  label: string
  values: Array<string | number>
  keys: Array<FlightFilterType | string>
}

export interface FlightFilterLabelDescriptor extends LabelDescriptor {
  type: FlightFilterType
}

interface BuildParams {
  filters: Record<string, Array<FilterPanelCheckItem>>
  activeFilters: Record<string, Set<string>>
  currency: { code: string, region: string }
  airports: { origin: string, arrival: string }
  priceFilterType: FlightsFiltersPriceType
  intl: IntlShape
  /** When true, arrival-time chips use origin (return leg lands back home). */
  shouldRenderCombinedTiles?: boolean
}

const messages = defineMessages({
  stopoverTimeUnder: {
    id: 'U34aFAQvr8xWcNR7e-yU',
    defaultMessage: 'Stopover time under {hours}h {minutes}m',
  },
  flightDurationUnder: {
    id: '-tvSF0HKR_SuRKMPdG6F',
    defaultMessage: 'Flight duration under {hours}h {minutes}m',
  },
  perAdultPriceBetween: {
    id: 'tYs6TxosFq1H6A2WOM4d',
    defaultMessage: 'Per adult price between {minPrice} - {maxPrice}',
  },
  totalPriceBetween: {
    id: 'LDz4nUByRqhtEs8sq-i_',
    defaultMessage: 'Total price between {minPrice} - {maxPrice}',
  },
  departFromTimeRange: {
    id: 'ZPHYqirVFmSJ7tPdFfzj',
    defaultMessage: 'Depart from {airport} {minDepartureTime} - {maxDepartureTime}',
  },
  arriveAtTimeRange: {
    id: 'Rhu0p97xNI7xVhEJmCXG',
    defaultMessage: 'Arrive at {airport} {minArrivalTime} - {maxArrivalTime}',
  },
})

const formatMessage = (
  intl: IntlShape,
  descriptor: MessageDescriptor,
  values?: Record<string, string | number>,
) => intl.formatMessage(descriptor, values)

export function getFlightFilterLabelDescriptors({
  filters,
  activeFilters,
  currency,
  airports,
  priceFilterType,
  intl,
  shouldRenderCombinedTiles,
}: BuildParams): Array<FlightFilterLabelDescriptor> {
  return STANDALONE_FLIGHT_FILTERS.flatMap(filterKey => {
    const descriptor = getFilterDescriptorForType(filterKey, {
      filters,
      activeFilters,
      currency,
      airports,
      priceFilterType,
      intl,
      shouldRenderCombinedTiles,
    })

    if (!descriptor) return []
    const descriptors = Array.isArray(descriptor) ? descriptor : [descriptor]

    return descriptors
      .filter(desc => Boolean(desc.label))
      .map(desc => ({
        ...desc,
        type: filterKey,
      }))
  })
}

function getFilterDescriptorForType(
  filterKey: FlightFilterType,
  {
    filters,
    activeFilters,
    currency,
    airports,
    priceFilterType,
    intl,
    shouldRenderCombinedTiles,
  }: BuildParams,
): LabelDescriptor | Array<LabelDescriptor> | undefined {
  const quantity = getFilterQuantity(activeFilters, filterKey)
  if (quantity === 0) {
    return undefined
  }

  if (filterKey === FlightFilterType.PRICES) {
    return buildPriceDescriptor(filters, activeFilters, currency, priceFilterType, intl)
  }

  if (filterKey === FlightFilterType.TIMES) {
    return buildTimeDescriptors(activeFilters, airports, intl, shouldRenderCombinedTiles)
  }

  if (filterKey === FlightFilterType.STOP_DURATIONS) {
    const value = firstSetValue(activeFilters[FlightFilterType.STOP_DURATIONS])
    if (!value) return undefined
    const time = convertMinutesToHoursAndMinutes(parseInt(value, 10))

    return {
      label: formatMessage(intl, messages.stopoverTimeUnder, { hours: time.hours, minutes: time.minutes }),
      values: [value],
      keys: [FlightFilterType.STOP_DURATIONS],
    }
  }

  if (filterKey === FlightFilterType.DURATIONS) {
    const value = firstSetValue(activeFilters[FlightFilterType.DURATIONS])
    if (!value) return undefined
    const time = convertMinutesToHoursAndMinutes(parseInt(value, 10))

    return {
      label: formatMessage(intl, messages.flightDurationUnder, { hours: time.hours, minutes: time.minutes }),
      values: [value],
      keys: [FlightFilterType.DURATIONS],
    }
  }

  const values = [...(activeFilters[filterKey] || new Set<string>())].filter(value => value !== 'all')
  if (!values.length) return undefined

  const labels = arrayToObject(
    filters[filterKey] || [],
    filter => filter.value,
    filter => filter.label as string,
  )

  return values.map(value => ({
    label: labels[value] ?? value,
    values: [value],
    keys: [filterKey],
  }))
}

function buildPriceDescriptor(
  filters: Record<string, Array<FilterPanelCheckItem>>,
  activeFilters: Record<string, Set<string>>,
  currency: { code: string, region: string },
  priceFilterType: FlightsFiltersPriceType,
  intl: IntlShape,
): LabelDescriptor | undefined {
  const lastPriceOption = last(filters[FlightFilterType.PRICES])
  const lastPriceValue = typeof lastPriceOption?.value === 'string'
    ? parseInt(lastPriceOption.value, 10)
    : lastPriceOption?.value

  const minPriceValue = firstSetValue(activeFilters[FlightFilterType.MIN_PRICES])
  const maxPriceValue = firstSetValue(activeFilters[FlightFilterType.MAX_PRICES]) ?? (lastPriceValue?.toString())

  if (!minPriceValue && !maxPriceValue) return undefined

  const parsedMin = typeof minPriceValue === 'string' ? parseInt(minPriceValue, 10) : minPriceValue ?? 0
  const parsedMax = typeof maxPriceValue === 'string' ? parseInt(maxPriceValue, 10) : maxPriceValue ?? lastPriceValue ?? 0

  const min = Math.floor(parsedMin / 100) * 100
  const max = Math.round(parsedMax / 100) * 100

  const minPrice = formatCurrency(min, currency.code, currency.region)
  const maxPrice = formatCurrency(max, currency.code, currency.region)

  return {
    label: priceFilterType === FlightsFiltersPriceType.ADULT
      ? formatMessage(intl, messages.perAdultPriceBetween, { minPrice, maxPrice })
      : formatMessage(intl, messages.totalPriceBetween, { minPrice, maxPrice }),
    values: [minPriceValue ?? '0', maxPriceValue ?? lastPriceValue ?? '0'],
    keys: [FlightFilterType.MIN_PRICES, FlightFilterType.MAX_PRICES],
  }
}

function buildTimeDescriptors(
  activeFilters: Record<string, Set<string>>,
  airports: { origin: string, arrival: string },
  intl: IntlShape,
  shouldRenderCombinedTiles?: boolean,
): Array<LabelDescriptor> | undefined {
  const descriptors: Array<LabelDescriptor> = []

  const minDepartValue = firstSetValue(activeFilters[FlightFilterType.MIN_DEPARTURE_TIME])
  const maxDepartValue = firstSetValue(activeFilters[FlightFilterType.MAX_DEPARTURE_TIME])

  if (minDepartValue || maxDepartValue) {
    const minDepartureTime = convertMinutesToTimeString(parseInt(minDepartValue || '0', 10))
    const maxDepartureTime = convertMinutesToTimeString(parseInt(maxDepartValue || '1440', 10))

    descriptors.push({
      label: formatMessage(intl, messages.departFromTimeRange, {
        airport: airports.origin,
        minDepartureTime,
        maxDepartureTime,
      }),
      values: [minDepartValue ?? '0', maxDepartValue ?? '1440'],
      keys: [FlightFilterType.MIN_DEPARTURE_TIME, FlightFilterType.MAX_DEPARTURE_TIME],
    })
  }

  const minOutboundArrivalValue = firstSetValue(activeFilters[FlightFilterType.MIN_OUTBOUND_ARRIVAL_TIME])
  const maxOutboundArrivalValue = firstSetValue(activeFilters[FlightFilterType.MAX_OUTBOUND_ARRIVAL_TIME])

  if (minOutboundArrivalValue || maxOutboundArrivalValue) {
    const minArrivalTime = convertMinutesToTimeString(parseInt(minOutboundArrivalValue || '0', 10))
    const maxArrivalTime = convertMinutesToTimeString(parseInt(maxOutboundArrivalValue || '1440', 10))

    descriptors.push({
      label: formatMessage(intl, messages.arriveAtTimeRange, {
        airport: airports.arrival,
        minArrivalTime,
        maxArrivalTime,
      }),
      values: [minOutboundArrivalValue ?? '0', maxOutboundArrivalValue ?? '1440'],
      keys: [FlightFilterType.MIN_OUTBOUND_ARRIVAL_TIME, FlightFilterType.MAX_OUTBOUND_ARRIVAL_TIME],
    })
  }

  const minReturnDepartureValue = firstSetValue(activeFilters[FlightFilterType.MIN_RETURN_DEPARTURE_TIME])
  const maxReturnDepartureValue = firstSetValue(activeFilters[FlightFilterType.MAX_RETURN_DEPARTURE_TIME])

  if (minReturnDepartureValue || maxReturnDepartureValue) {
    const minDepartureTime = convertMinutesToTimeString(parseInt(minReturnDepartureValue || '0', 10))
    const maxDepartureTime = convertMinutesToTimeString(parseInt(maxReturnDepartureValue || '1440', 10))

    descriptors.push({
      label: formatMessage(intl, messages.departFromTimeRange, {
        airport: airports.arrival,
        minDepartureTime,
        maxDepartureTime,
      }),
      values: [minReturnDepartureValue ?? '0', maxReturnDepartureValue ?? '1440'],
      keys: [FlightFilterType.MIN_RETURN_DEPARTURE_TIME, FlightFilterType.MAX_RETURN_DEPARTURE_TIME],
    })
  }

  const minArrivalValue = firstSetValue(activeFilters[FlightFilterType.MIN_ARRIVAL_TIME])
  const maxArrivalValue = firstSetValue(activeFilters[FlightFilterType.MAX_ARRIVAL_TIME])

  if (minArrivalValue || maxArrivalValue) {
    const minArrivalTime = convertMinutesToTimeString(parseInt(minArrivalValue || '0', 10))
    const maxArrivalTime = convertMinutesToTimeString(parseInt(maxArrivalValue || '1440', 10))
    // Combined/multi-tile return arrival lands back at origin; one-way arrival uses destination.
    const arrivalAirport = shouldRenderCombinedTiles ? airports.origin : airports.arrival

    descriptors.push({
      label: formatMessage(intl, messages.arriveAtTimeRange, {
        airport: arrivalAirport,
        minArrivalTime,
        maxArrivalTime,
      }),
      values: [minArrivalValue ?? '0', maxArrivalValue ?? '1440'],
      keys: [FlightFilterType.MIN_ARRIVAL_TIME, FlightFilterType.MAX_ARRIVAL_TIME],
    })
  }

  return descriptors.length ? descriptors : undefined
}

function firstSetValue(set?: Set<string>) {
  if (!set || set.size === 0) return undefined
  const [value] = set
  return value
}
